'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { Box, Container, Grid, HStack, Skeleton, SimpleGrid, Stack, VStack } from '@chakra-ui/react';
import { SkeletonCircle, SkeletonText } from '@/components/ui/skeleton';
import SkeletonCard from '@/_components/Skeleton/Card';

export function BrandSingleSkeleton() {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <BrandSingleSkeletonDesktop />
  ) : (
    <BrandSingleSkeletonMobile />
  );
}

export function BrandSingleSkeletonDesktop() {
  return (
    <Box bg="background" minH="100dvh" pb={10}>
      {/* Header skeleton */}
      <Box px={10} pt="38px" pb={4}>
        <HStack justify="space-between" mb={4}>
          <Skeleton w="120px" h="40px" rounded={12} />
          <HStack gap={3}>
            <SkeletonCircle size="10" />
            <Skeleton w="140px" h="36px" rounded={12} />
          </HStack>
        </HStack>
      </Box>

      <Container pt={0} px={10}>
        <Grid gap={5} templateColumns="250px 1fr 328px">
          {/* Left Sidebar */}
          <Box display={{ base: 'none', lg: 'block' }}>
            <Stack gap={3}>
              <SkeletonText noOfLines={1} w="80px" />
              <Skeleton h="48px" rounded={12} />
              <Skeleton h="48px" rounded={12} />
              <SkeletonText noOfLines={1} w="60px" mt={4} />
              <Skeleton h="44px" rounded={12} />
              <SkeletonText noOfLines={1} w="50px" mt={4} />
              <Skeleton h="36px" rounded={12} />
              <Skeleton h="36px" rounded={12} />
              <Skeleton h="36px" rounded={12} />
              <Skeleton h="36px" rounded={12} />
            </Stack>
          </Box>

          {/* Main Content */}
          <Box pr={4}>
            <VStack align="stretch" gap={6}>
              <Skeleton
                w="full"
                h={{ base: '120px', lg: '200px' }}
                rounded={24}
                aspectRatio={4 / 2}
              />
              <VStack align="stretch" gap={8}>
                <SkeletonText noOfLines={1} w="120px" />
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4}>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </SimpleGrid>
                <SkeletonText noOfLines={1} w="100px" />
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4}>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </SimpleGrid>
              </VStack>
            </VStack>
          </Box>

          {/* Right Cart */}
          <Box
            as="aside"
            position="sticky"
            top="125px"
            alignSelf="start"
            display={{ base: 'none', lg: 'block' }}
          >
            <Box bg="white" rounded={16} p={4}>
              <SkeletonText noOfLines={1} w="60px" mb={4} />
              <Stack gap={3}>
                <CartRowSkeleton />
                <CartRowSkeleton />
                <CartRowSkeleton />
              </Stack>
              <Skeleton h="48px" rounded={12} mt={4} w="full" />
            </Box>
          </Box>
        </Grid>
      </Container>
    </Box>
  );
}

export function BrandSingleSkeletonMobile() {
  return (
    <Box bg="background" minH="100dvh" pb="70px">
      {/* Restaurant header skeleton */}
      <Box px={2} shadow="md" borderBottomRadius="xl" bg="white">
        <HStack justify="space-between" py="2">
          <SkeletonCircle size="10" />
          <SkeletonCircle size="10" />
        </HStack>
        <Box p={4} pt={2}>
          <Skeleton h="40px" rounded={12} w="full" mb={3} />
          <HStack gap={2}>
            <SkeletonCircle size="8" />
            <Stack flex={1} gap={1}>
              <Skeleton h="14px" w="80px" rounded={4} />
              <Skeleton h="12px" w="60px" rounded={4} />
            </Stack>
          </HStack>
        </Box>
      </Box>

      {/* Categories horizontal scroll */}
      <HStack
        gap={2}
        px={4}
        pt={4}
        overflowX="auto"
        scrollbar="hidden"
        flexWrap="nowrap"
      >
        <Skeleton w="72px" h="72px" rounded={16} flexShrink={0} />
        <Skeleton w="72px" h="72px" rounded={16} flexShrink={0} />
        <Skeleton w="72px" h="72px" rounded={16} flexShrink={0} />
        <Skeleton w="72px" h="72px" rounded={16} flexShrink={0} />
        <Skeleton w="72px" h="72px" rounded={16} flexShrink={0} />
      </HStack>

      {/* Product sections */}
      <Stack p={4} gap={6}>
        <VStack align="stretch" gap={3}>
          <Skeleton h="24px" w="100px" rounded={4} />
          <SimpleGrid columns={2} gap={2}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </SimpleGrid>
        </VStack>
        <VStack align="stretch" gap={3}>
          <Skeleton h="24px" w="80px" rounded={4} />
          <SimpleGrid columns={2} gap={2}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </SimpleGrid>
        </VStack>
      </Stack>
    </Box>
  );
}

function CartRowSkeleton() {
  return (
    <HStack gap={3} align="center">
      <Skeleton
        minW="48px"
        w="48px"
        h="48px"
        rounded={8}
        flexShrink={0}
      />
      <VStack align="stretch" flex={1} gap={1}>
        <Skeleton h="14px" w="full" maxW="120px" rounded={4} />
        <Skeleton h="12px" w="60px" rounded={4} />
      </VStack>
      <Skeleton h="32px" w="80px" rounded={8} flexShrink={0} />
    </HStack>
  );
}
